import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import {
  useAdminFeedbacks,
  FEEDBACK_CATEGORIES,
  FEEDBACK_STATUS,
  type Feedback,
  type FeedbackStatus,
} from '../../../hooks/useFeedback';

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

const STATUS_OPTIONS: FeedbackStatus[] = ['novo', 'lido', 'em_analise', 'resolvido'];

const statusColor: Record<string, string> = {
  novo:       'bg-sky-500/20 text-sky-400',
  lido:       'bg-white/10 text-white/50',
  em_analise: 'bg-yellow-500/20 text-yellow-400',
  resolvido:  'bg-emerald-500/20 text-emerald-400',
};

const catColor: Record<string, string> = {
  bug:       'bg-red-500/20 text-red-400',
  duvida:    'bg-sky-500/20 text-sky-400',
  melhoria:  'bg-violet-500/20 text-violet-400',
  elogio:    'bg-emerald-500/20 text-emerald-400',
  cobranca:  'bg-orange-500/20 text-orange-400',
  outro:     'bg-white/10 text-white/40',
};

export default function AdminFeedbacksPage() {
  const { feedbacks, loading, updateStatus } = useAdminFeedbacks();

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCat, setFilterCat] = useState('all');
  const [selected, setSelected] = useState<Feedback | null>(null);
  const [newStatus, setNewStatus] = useState<FeedbackStatus>('lido');
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [profileMap, setProfileMap] = useState<Record<string, { name: string; email: string }>>({});
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (feedbacks.length === 0) return;
    const ids = [...new Set(feedbacks.map(f => f.user_id))];
    supabase.from('profiles').select('id, full_name, display_name, email')
      .in('id', ids)
      .then(({ data }) => {
        const map: Record<string, { name: string; email: string }> = {};
        (data ?? []).forEach((p: { id: string; full_name: string; display_name: string; email: string }) => {
          map[p.id] = { name: p.full_name || p.display_name || 'Usuário', email: p.email || '' };
        });
        setProfileMap(map);
      });
  }, [feedbacks]);

  const filtered = feedbacks.filter(f => {
    if (filterStatus !== 'all' && f.status !== filterStatus) return false;
    if (filterCat !== 'all' && f.category !== filterCat) return false;
    return true;
  });

  const counts = {
    total:      feedbacks.length,
    novo:       feedbacks.filter(f => f.status === 'novo').length,
    em_analise: feedbacks.filter(f => f.status === 'em_analise').length,
    resolvido:  feedbacks.filter(f => f.status === 'resolvido').length,
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    await updateStatus(selected.id, newStatus, adminNotes.trim() || undefined);
    setSaving(false);
    setSelected(null);
    setToast('Status atualizado com sucesso!');
    setTimeout(() => setToast(null), 2500);
  };

  const openDetail = (fb: Feedback) => {
    setSelected(fb);
    setNewStatus(fb.status);
    setAdminNotes(fb.admin_notes ?? '');
  };

  const catInfo = (val: string) => FEEDBACK_CATEGORIES.find(c => c.value === val);
  const stInfo  = (val: string) => FEEDBACK_STATUS.find(s => s.value === val);

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div className="fixed top-5 right-5 z-50 px-4 py-3 bg-emerald-500 text-white rounded-xl text-sm font-500 flex items-center gap-2 pointer-events-none">
          <i className="ri-check-line" />{toast}
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-[24px] font-700 text-white">Feedbacks</h1>
          <p className="text-white/40 text-[14px] mt-1">Canal privado de comunicação com os usuários</p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/60 text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line" />Atualizar
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total',       value: counts.total,      icon: 'ri-feedback-line',       color: 'bg-white/10',      text: 'text-white' },
          { label: 'Novos',       value: counts.novo,       icon: 'ri-mail-unread-line',     color: 'bg-sky-600',       text: 'text-sky-300' },
          { label: 'Em análise',  value: counts.em_analise, icon: 'ri-search-eye-line',      color: 'bg-yellow-600',    text: 'text-yellow-300' },
          { label: 'Resolvidos',  value: counts.resolvido,  icon: 'ri-check-double-line',    color: 'bg-emerald-600',   text: 'text-emerald-300' },
        ].map(s => (
          <div key={s.label} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-4">
            <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${s.color}`}>
              <i className={`${s.icon} text-white text-[20px]`} />
            </div>
            <div>
              <p className="text-[11px] text-white/40 mb-0.5">{s.label}</p>
              <p className={`text-[22px] font-700 ${s.text}`}>{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-5 space-y-3">
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-[11px] text-white/30 uppercase tracking-wider font-600 mr-1">Status</span>
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${filterStatus === 'all' ? 'bg-violet-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
          >
            Todos
          </button>
          {FEEDBACK_STATUS.map(s => (
            <button
              key={s.value}
              onClick={() => setFilterStatus(s.value)}
              className={`px-3 py-1.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${filterStatus === s.value ? 'bg-violet-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex gap-3 flex-wrap items-center">
          <span className="text-[11px] text-white/30 uppercase tracking-wider font-600 mr-1">Categoria</span>
          <select
            value={filterCat}
            onChange={e => setFilterCat(e.target.value)}
            className="bg-white/5 border border-white/10 text-white/60 text-[12px] rounded-xl px-3 py-1.5 outline-none cursor-pointer"
          >
            <option value="all">Todas as categorias</option>
            {FEEDBACK_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      {/* List */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line text-2xl text-violet-400 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/30">
            <i className="ri-inbox-line text-3xl mb-2 block" />
            <p className="text-[14px]">Nenhum feedback encontrado</p>
            <p className="text-[12px] mt-1 text-white/20">
              {feedbacks.length === 0 ? 'Nenhum feedback enviado ainda.' : 'Nenhum resultado para os filtros aplicados.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map(fb => {
              const cat = catInfo(fb.category);
              const st  = stInfo(fb.status);
              const author = profileMap[fb.user_id];
              return (
                <div
                  key={fb.id}
                  onClick={() => openDetail(fb)}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-white/[0.03] cursor-pointer transition-colors"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${catColor[fb.category] || 'bg-white/10 text-white/40'}`}>
                    <i className={`${cat?.icon ?? 'ri-chat-1-line'} text-[13px]`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-700 text-white truncate">{fb.subject}</p>
                    <p className="text-[11px] text-white/40 truncate mt-0.5">
                      {author?.name ?? 'Usuário'} · {cat?.label} · {fmtDate(fb.created_at)}
                    </p>
                  </div>
                  <span className={`text-[11px] font-600 px-2.5 py-0.5 rounded-full whitespace-nowrap ${statusColor[fb.status] || 'bg-white/10 text-white/40'}`}>
                    {st?.label ?? fb.status}
                  </span>
                  <i className="ri-arrow-right-s-line text-white/30" />
                </div>
              );
            })}
          </div>
        )}
        <div className="px-5 py-3 border-t border-white/5 text-[12px] text-white/30">
          {filtered.length} feedback{filtered.length !== 1 ? 's' : ''} exibido{filtered.length !== 1 ? 's' : ''}
          {feedbacks.length !== filtered.length && ` (de ${feedbacks.length} total)`}
        </div>
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#1a1a2e]">
              <div>
                <h2 className="text-[16px] font-700 text-white">{selected.subject}</h2>
                <p className="text-[12px] text-white/30 mt-0.5">
                  {profileMap[selected.user_id]?.name ?? 'Usuário'} · {profileMap[selected.user_id]?.email} · {fmtDate(selected.created_at)}
                </p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-white/60" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Category badge */}
              <div className="flex items-center gap-2">
                <span className={`text-[11px] font-600 px-2.5 py-0.5 rounded-full ${catColor[selected.category] || 'bg-white/10 text-white/40'}`}>
                  {catInfo(selected.category)?.label ?? selected.category}
                </span>
              </div>

              {/* Message */}
              <div className="bg-white/5 rounded-xl p-4">
                <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-2">Mensagem</p>
                <p className="text-[13px] text-white/80 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>

              {/* Change status */}
              <div>
                <p className="text-[12px] text-white/40 font-600 uppercase tracking-wider mb-2">Alterar status</p>
                <div className="grid grid-cols-2 gap-2">
                  {STATUS_OPTIONS.map(s => {
                    const si = stInfo(s);
                    const active = newStatus === s;
                    return (
                      <button
                        key={s}
                        onClick={() => setNewStatus(s)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-[12px] font-600 cursor-pointer transition-colors ${
                          active
                            ? `${statusColor[s]} border-current`
                            : 'border-white/10 text-white/50 hover:bg-white/5'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          s === 'resolvido' ? 'bg-emerald-400' :
                          s === 'em_analise' ? 'bg-yellow-400' :
                          s === 'lido' ? 'bg-white/30' : 'bg-sky-400'
                        }`} />
                        {si?.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Admin notes */}
              <div>
                <p className="text-[12px] text-white/40 font-600 uppercase tracking-wider mb-2">Resposta para o usuário</p>
                <textarea
                  value={adminNotes}
                  onChange={e => setAdminNotes(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="Escreva uma resposta que o usuário verá no histórico..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 resize-none"
                />
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-3">
              <button
                onClick={() => setSelected(null)}
                className="flex-1 py-2.5 border border-white/10 text-white/60 font-600 rounded-xl text-[13px] cursor-pointer hover:bg-white/5 whitespace-nowrap transition-colors"
              >
                Fechar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white font-600 rounded-xl text-[13px] cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 transition-colors"
              >
                {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
